import { useState, useRef, useEffect, useMemo } from 'react';
import { D, st, toArr, getToken, apiGet, apiPost, EXPENSE_CATEGORIES, QUICK_CATEGORIES, SCHEDULE_C } from '../constants';
import { billUnitFor, packageOf, stickOf } from '../lib/priceSheet.js';
import { Field } from './shared';

// A line's destination IS its target: 'job:<id>' | 'inv:<id>' | 'expense' |
// 'truck' | '' (unset). The choice lives on the line, not once per receipt.
const isBillable = d => /^(job|inv):/.test(String(d || ''));
// SHAPE WAS NEVER THE PROBLEM - '2026-01-01' passes any format check. The
// 7-Eleven scan put October fuel into January and nothing questioned it,
// because the only test was the regex below. This judges PLAUSIBILITY and
// warns; it never blocks, since catching up on an older receipt is normal.
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const dateVerdict = (d) => {
  if (!d || !DATE_RE.test(d)) return 'missing';
  const t = new Date(d + 'T00:00:00');
  if (isNaN(t.getTime())) return 'missing';
  const days = Math.round((Date.now() - t.getTime()) / 86400000);
  if (days < -1) return 'future';
  if (days > 45) return 'old';
  return 'ok';
};
const isPersonal = d => String(d || '') === 'personal';

// Supply houses that price linear goods per 100 or per 1,000 ft print FEET in
// the quantity column, so their unit cost is ALREADY per foot. Proven for CED
// by a real packing slip: 30 x ($149.00 / 100) = $44.70, the printed extension.
// Retail sells sticks and coils by the piece. Seeded with CED alone on purpose
// - it is the only supplier there is evidence for, and an assumption should
// land on the side that gets checked.
const PER_FOOT_SUPPLIERS = /\b(CED|RAYBRO)\b/i;
const r6 = n => Math.round(n * 1e6) / 1e6;

// 🔑 ONE AUTOMATIC RETRY BEFORE BOTHERING ANYONE. The failure is intermittent,
// not a hard limit: two uploads died with "Failed to fetch" without reaching
// nginx at all, and a third on the identical code path went through minutes
// later. A blip usually clears itself, so the first recovery should cost the
// user nothing.
const uploadReceiptPhoto = async (receiptId, photo) => {
  const send = () => apiPost('/data/receipts/' + receiptId + '/image', { data: photo.data, name: photo.name });
  try { await send(); return { ok: true, tries: 1 }; }
  catch (first) {
    await new Promise(r => setTimeout(r, 1500));
    try { await send(); return { ok: true, tries: 2 }; }
    catch (second) { return { ok: false, tries: 2, error: (second && second.message) || String(second) }; }
  }
};

// 🔑 ONE definition of what a scanned line becomes in the price book, used by
// BOTH the on-screen note and the save. Two copies of this rule is exactly how
// invoiceTotal ended up computed three different ways.
const bookPriceFor = (it, supplierName, standing) => {
  const cls = billUnitFor(it.desc);
  const named = packageOf(it.desc, cls.unit);
  // An explicitly named package wins - it is on the document, not inferred.
  if (named && named.qty > 1) {
    return { price: r6(it.unitCost / named.qty), unit: 'ft', converted: true,
             via: named.label, pkgQty: named.qty, pkgLabel: named.label };
  }
  const stick = stickOf(it.desc, cls.unit);
  if (stick) {
    let asStick = !PER_FOOT_SUPPLIERS.test(String(supplierName || ""));
    // The standing price is EVIDENCE and outranks the supplier assumption:
    // whichever reading lands closer to what this material has always cost is
    // the right one. That is what catches a new supplier who prices per foot.
    if (standing > 0) {
      const raw = Math.abs(it.unitCost / standing - 1);
      const div = Math.abs((it.unitCost / stick.qty) / standing - 1);
      asStick = div < raw;
    }
    if (asStick) {
      return { price: r6(it.unitCost / stick.qty), unit: 'ft', converted: true,
               via: stick.label, pkgQty: stick.qty, pkgLabel: stick.label };
    }
  }
  const unit = named ? 'ft' : (cls.uncertain ? (it.unit || 'ea') : cls.unit);
  return { price: it.unitCost, unit, converted: false, via: null,
           pkgQty: named ? named.qty : null, pkgLabel: named ? named.label : null };
};
// The category a destination implies. Personal has exactly one correct
// category, and switching AWAY from personal must not leave the line claiming
// to be a personal electrical material - so it resets in both directions.
const destCategory = (dest, cur) => {
  if (isPersonal(dest)) return 'Personal';
  if (cur === 'Personal') return dest === 'expense' ? 'Other' : 'Materials';
  return dest === 'expense' && cur === 'Materials' ? 'Other' : cur;
};
const destKind = d => String(d || '').split(':')[0];
const destRef = d => Number(String(d || '').split(':')[1]) || null;
const FIXED_DESTS = [
  { id: 'expense', label: 'Business Expense (overhead)' },
  { id: 'truck', label: 'Truck Stock' },
  { id: 'personal', label: 'Personal - not a business expense' },
];

const money = n => '$' + (Number(n) || 0).toFixed(2);

// A phone photo is 3-5 MB and goes out TWICE as base64 (once to the vision
// model, once to R2), which inflates it another third. Shrinking it here cuts
// the upload, the token cost, and the wait on a phone.
//
// THESE TWO NUMBERS COME FROM THE VISION DOCS, NOT FROM TASTE.
// Claude downsizes any image above its tier's long-edge cap before the model
// sees it: 2,576 px for Claude 4.7+ models (this file calls claude-sonnet-5),
// 1,568 px for older ones. Sending a 4000 px photo is pure waste - the detail
// is discarded server-side. Landing exactly ON the cap means the API does not
// resample at all, so the image is resized ONCE in the whole path, not twice.
//
// Quality is 0.92, not the usual 0.8-ish, because the docs warn about exactly
// this case: heavy JPEG compression makes text hard to read, especially with
// multiple passes. A receipt IS text, and the phone already wrote one JPEG -
// ours is pass two. 0.92 at 2576 px still lands a 4 MB photo well under a
// megabyte, which is all the upload needed.
const MAX_EDGE = 2576;
const JPEG_QUALITY = 0.92;
const COMPRESS_OVER = 900 * 1024; // smaller than this is left completely alone

const readAsBase64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = ev => resolve(String(ev.target.result).split(',')[1]);
  r.onerror = reject;
  r.readAsDataURL(blob);
});

// Returns {data, type, bytes}. Falls back to the ORIGINAL on any failure: a
// compression problem must never cost someone their receipt.
const prepareImage = async (file) => {
  const original = async () => ({
    data: await readAsBase64(file),
    type: file.type || 'image/jpeg',
    bytes: file.size || 0,
  });
  if (!file || !/^image\//.test(file.type || '')) return original();
  if ((file.size || 0) <= COMPRESS_OVER) return original();
  try {
    // imageOrientation matters: a phone photo often relies on an EXIF rotation
    // tag, and drawing to a canvas strips it. Without this the receipt comes
    // out sideways - worse than the problem being solved.
    const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, MAX_EDGE / Math.max(bmp.width, bmp.height));
    const w = Math.max(1, Math.round(bmp.width * scale));
    const h = Math.max(1, Math.round(bmp.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bmp, 0, 0, w, h);
    if (bmp.close) bmp.close();
    const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', JPEG_QUALITY));
    if (!blob || blob.size >= (file.size || Infinity)) return original();
    return { data: await readAsBase64(blob), type: 'image/jpeg', bytes: blob.size };
  } catch (e) {
    return original();
  }
};

export default function ScanInvoice({ me, clients, invoices, jobs, clientName, openInvoiceModal, markup, apiSaveExpense,
  matPrices = {}, matPriceHistory = {}, setMatPriceHistory = () => {}, apiSaveMatPrices = null }) {
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [vendor, setVendor] = useState('');
  const [items, setItems] = useState(null);
  const [jobId, setJobId] = useState('');
  const [truckId, setTruckId] = useState('');
  const [trucks, setTrucks] = useState([]);
  const [addToInvoice, setAddToInvoice] = useState(false);
  const [target, setTarget] = useState('new');
  const [invoiceId, setInvoiceId] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [savePrices, setSavePrices] = useState(false);
  // The photo itself. It used to be read, sent to the model, and thrown away -
  // so every expense was a number with no substantiating document, which is
  // the thing the IRS actually wants. Held here until the receipt row exists,
  // then uploaded to R2 against it.
  const [photo, setPhoto] = useState(null);
  // A FAILED UPLOAD MUST NOT TAKE THE PHOTO WITH IT. The bytes are already in
  // the browser, the receipt row exists, and the endpoint to attach an image
  // later has existed all along - what was missing was anything holding on to
  // the image. Receipt 5 has no document for exactly this reason: the save
  // succeeded, the upload did not, and then setPhoto(null) dropped the only
  // copy on the floor.
  const [pendingPhoto, setPendingPhoto] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [purchaseDate, setPurchaseDate] = useState('');
  const [dateNote, setDateNote] = useState('');
  // Tax comes OFF the receipt. A configured rate is a fallback for a FAILED
  // READ, never a normal path - substituting one hides a bad scan behind a
  // plausible number. taxSource records which happened so the two stay
  // distinguishable later.
  const [tax, setTax] = useState('');
  const [taxSource, setTaxSource] = useState('read');
  const [printedTotal, setPrintedTotal] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { apiGet('/data/trucks').then(setTrucks).catch(() => {}); }, []);

  const matMarkup = markup?.Materials || 20;
  // jobId now holds a TOKEN: 'job:<id>' or 'inv:<id>'. One state, two kinds of
  // target, so nothing else in the component needed re-plumbing.
  const billKind = String(jobId || '').split(':')[0];
  const billRef  = Number(String(jobId || '').split(':')[1]) || null;
  const job = billKind === 'job' ? toArr(jobs).find(j => j.id === billRef) : null;
  const billInv = billKind === 'inv' ? toArr(invoices).find(i => i.id === billRef) : null;
  const jobClientId = job ? (job.clientId ?? job.client_id ?? null)
                    : billInv ? (billInv.clientId ?? billInv.client_id ?? null) : null;
  const draftInvoices = toArr(invoices).filter(i => i.status === 'Draft' && (i.clientId ?? i.client_id) === jobClientId);

  // Suppliers already in the price history, most recent first - the same
  // picker the price-sheet importer uses. A name the scanner reads is matched
  // against these before being used, so "CED" cannot become a second entry
  // beside "CED Naples".
  const knownSuppliers = useMemo(() => {
    const seen = new Map();
    for (const arr of Object.values(matPriceHistory || {})) {
      if (!Array.isArray(arr)) continue;
      for (const e of arr) {
        if (!e || !e.supplier) continue;
        const prev = seen.get(e.supplier) || '';
        if (String(e.date || '') >= prev) seen.set(e.supplier, String(e.date || ''));
      }
    }
    return [...seen.entries()].sort((a, b) => b[1].localeCompare(a[1])).map(x => x[0]);
  }, [matPriceHistory]);

  const reset = () => {
    setItems(null); setVendor(''); setJobId(''); setTruckId(''); setAddToInvoice(false); setTarget('new');
    setInvoiceId(''); setSaveError(''); setSaved(false); setSavePrices(false);
    setPhoto(null); setTax(''); setTaxSource('read'); setPrintedTotal(null); setPurchaseDate('');
  };

  const scanReceipt = async (file) => {
    setScanning(true); setScanError(''); reset();
    try {
      const img = await prepareImage(file);
      const b64 = img.data;
      setPhoto({ data: b64, name: file.name || 'receipt.jpg', type: img.type, bytes: img.bytes });

      const prompt = `You are reading a supply house packing slip or retail receipt. The image may be rotated — read it regardless of orientation.

TASK: Extract purchased line items and return valid JSON only. No explanation, no markdown.

VENDOR — read this carefully. The vendor is the SUPPLIER WHO ISSUED this document: the business you bought FROM, normally in the letterhead, logo or return address.
- It is NEVER the "SOLD TO", "SHIP TO", "BILL TO" or "CUSTOMER" name. Those are the BUYER.
- On a supply house slip the buyer's name is often large and central while the supplier's is small. Do not be fooled by prominence.
- If you cannot identify the supplier with confidence, return "" for vendor. An empty field is better than the wrong company.

TOTALS: read the printed SUBTOTAL, SALES TAX and TOTAL if present. Use null for any you cannot read — never estimate or compute them.
- Supply houses label these differently from retail receipts. Treat MERCHANDISE, MATERIAL, GOODS or PRODUCT TOTAL as the SUBTOTAL. Treat TOTAL DUE, AMOUNT DUE, BALANCE DUE or INVOICE TOTAL as the TOTAL.
- Tax may be printed as "Tax @ 6.00000%", "SALES TAX 6%", "FL TAX" or just "TAX". Take the DOLLAR AMOUNT next to it, never the percentage. A line reading "Tax @ 6.00000%    23.44" means tax is 23.44, not 6.
- If a SHIPPING, FREIGHT, DELIVERY or HANDLING charge is printed and is NOT zero, include it as a line item using that exact word as the description, qty 1, unitCost = the amount. It is real money on the receipt and the line items must still sum to the total.
- Sanity check before answering: subtotal + tax (+ shipping) should equal the printed total. If they do not, you have read one of them off the wrong line - re-read rather than adjusting a number to make it fit.

DATE: the transaction date printed on the receipt, as YYYY-MM-DD.
- US receipts print MM/DD/YYYY. 11/01/2026 is 1 November 2026, never 11 January.
- A 2-digit year means 20xx: 10/31/26 is 2026-10-31.
- Use the PURCHASE date — not an expiry, "best by", "valid until", return-by or promotional date.
- If you cannot read it clearly, return null. NEVER guess, and never substitute today's date. A null is better than a wrong date: a wrong date files the expense in the wrong month and nothing downstream can detect it.

For columnar supply house invoices with columns like QUANTITY ORDERED | PRODUCT CODE | DESCRIPTION | QTY SHIPPED | PRICE | DISC | EXTENSION:
- DESCRIPTION column = human-readable item names (NOT product codes like LEV, ARF, LBCO)
- QUANTITY ORDERED = qty (the leftmost number column)
- UNIT COST = EXTENSION column divided by QUANTITY ORDERED (this gives actual cost per piece)
  Supply house invoices use pricing units: E=each, C=per 100, M=per 1000. Using EXTENSION/QTY bypasses this entirely.
- Do NOT use the PRICE column directly — it may be priced per C (hundred) or M (thousand)

Do NOT include column headers, subtotals, tax or totals as line items.

Return ONLY this JSON, nothing else:
{"vendor":"supplier name or empty string","date":"YYYY-MM-DD or null","subtotal":number or null,"tax":number or null,"total":number or null,"items":[{"desc":"item description","qty":number,"unit":"ea","unitCost":number}]}

Replace any inch marks in descriptions with the word in (e.g. 2in SCREW not 2" SCREW).
Use "ft" for wire/conduit sold by length. If unreadable: {"vendor":"","items":[]}.`;

      const resp = await fetch('/api/ai/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + getToken() },
        body: JSON.stringify({
          model: 'claude-sonnet-5', max_tokens: 2000,
          context: 'receipt_scan_invoice',
          messages: [{ role: 'user', content: [
            { type: 'image', source: { type: 'base64', media_type: img.type, data: b64 } },
            { type: 'text', text: prompt },
          ] }],
        }),
      });
      const data = await resp.json();
      if (data.error) throw new Error(data.error.message || 'AI error');
      const text = (data.content || []).map(c => c.text || '').join('');
      // Parse what the model sent FIRST. Only if that fails fall back to the
      // inch-mark repair, which is lossy: it cannot distinguish an inch mark
      // from a string's closing quote, so it corrupts any description ending
      // in a digit. It is a last resort, not a pre-processing step.
      const cleaned = text.replace(/```json|```/g, '').trim();
      let parsed;
      try {
        parsed = JSON.parse(cleaned);
      } catch (firstErr) {
        try {
          parsed = JSON.parse(cleaned.replace(/(\d)"/g, '$1in'));
        } catch (secondErr) {
          throw firstErr;   // report the real problem, not the repair's
        }
      }

      const parsedItems = toArr(parsed.items).map(it => {
        const desc = it.desc || 'Item';
        // The model is asked for ft on wire and conduit and routinely answers
        // "ea" anyway - a real CED slip came back with 30 FEET of EMT marked
        // each. billUnitFor is the classifier that got all 2,484 rows of the
        // price sheet right, so trust it over the model and keep the model's
        // answer only where the classifier has no opinion.
        const cls = billUnitFor(desc);
        // A package size in the NAME means the receipt's quantity counts packages,
        // not feet: "2" on a line reading 16ft ROLL is two rolls. Record what was
        // actually bought - the per-foot number is derived when the price book is
        // written, which is the only place billing reads from.
        const pkgHere = packageOf(desc, cls.unit);
        return {
          desc, qty: Number(it.qty) || 1,
          unit: pkgHere ? 'ea' : (cls.uncertain ? (it.unit || 'ea') : cls.unit),
          unitCost: Number(it.unitCost) || 0,
          category: 'Materials', destination: '', billable: true,
        };
      });
      if (parsedItems.length === 0) { setScanError("Couldn't find any line items on that receipt."); setScanning(false); return; }

      // A supplier the scan names is matched against ones already known before
      // being accepted, so spelling variants cannot multiply.
      const hint = String(parsed.vendor || '').trim();
      const hit = hint && knownSuppliers.find(s => hint.toLowerCase().includes(s.toLowerCase()) || s.toLowerCase().includes(hint.toLowerCase()));
      setVendor(hit || hint || knownSuppliers[0] || '');
      const dv = dateVerdict(parsed.date);
      setDateNote(dv);
      setPurchaseDate(dv === 'missing' ? new Date().toISOString().slice(0, 10) : parsed.date);
      if (parsed.tax != null && Number(parsed.tax) >= 0) { setTax(String(Number(parsed.tax))); setTaxSource('read'); }
      else { setTax(''); setTaxSource('estimated'); }
      if (parsed.total != null) setPrintedTotal(Number(parsed.total));
      setItems(parsedItems);
    } catch (e) { setScanError("Couldn't read receipt: " + e.message); }
    setScanning(false);
  };

  const onFileChange = e => { const file = e.target.files?.[0]; if (file) scanReceipt(file); e.target.value = ''; };
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e) => { e.preventDefault(); setDragOver(false); const file = e.dataTransfer.files[0]; if (file && file.type.startsWith('image/')) scanReceipt(file); };
  const updateItem = (i, patch) => setItems(prev => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  // What this material is already priced at, if anything. Lets a receipt scan
  // be checked against the price book before it overwrites it - the same
  // comparison the sheet importer shows in its Current Price column.
  const knownPrice = (desc) => (matPrices || {})[desc] ?? matPriceHistory?.[desc]?.[0]?.price ?? null;
  const removeItem = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));
  // Changing destination carries the sensible billable default with it: job
  // material is normally rebilled, overhead and truck stock are not.
  const setAllDestinations = (dest) => setItems(prev => prev.map(it => ({ ...it, destination: dest, billable: isBillable(dest), category: destCategory(dest, it.category) })));
  const setAllCategories = (cat) => setItems(prev => prev.map(it => ({ ...it, category: cat })));

  const jobItems = items ? items.filter(it => isBillable(it.destination)) : [];
  const unset = items ? items.filter(it => !it.destination) : [];
  // Each line's client comes from ITS OWN target, not a receipt-wide one.
  const clientForDest = (d) => {
    const r = destRef(d);
    if (destKind(d) === 'job') { const j = toArr(jobs).find(x => x.id === r); return j ? (j.clientId ?? j.client_id ?? null) : null; }
    if (destKind(d) === 'inv') { const v = toArr(invoices).find(x => x.id === r); return v ? (v.clientId ?? v.client_id ?? null) : null; }
    return null;
  };
  const truckItems = items ? items.filter(it => it.destination === 'truck') : [];
  // Shown on the totals strip so the money you cannot deduct is visible at the
  // moment you save, not discovered at year end.
  const personalTotal = items ? items.filter(it => isPersonal(it.destination))
    .reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unitCost) || 0), 0) : 0;
  const linesSubtotal = items ? items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unitCost) || 0), 0) : 0;
  const taxNum = Number(tax) || 0;
  const computedTotal = linesSubtotal + taxNum;
  // Reported, never enforced. A mismatch means a human should look - a missed
  // line, a wrong quantity, a slipped decimal - not that the save should fail.
  const totalGap = printedTotal != null ? computedTotal - printedTotal : null;
  const totalsOff = totalGap != null && Math.abs(totalGap) > Math.max(0.02, items ? items.length * 0.01 : 0.02);
  const jobTotal = jobItems.reduce((s, it) => s + it.qty * it.unitCost, 0);
  // Display only - the stored rates come from the server's distributeTax.
  // Same proportional rule, so the preview matches what actually lands.
  const jobTaxShare = linesSubtotal > 0 ? taxNum * (jobTotal / linesSubtotal) : 0;
  const billedAmount = Math.round((jobTotal + jobTaxShare) * (1 + matMarkup / 100) * 100) / 100;

  const save = async () => {
    if (!items || items.length === 0) return;
    if (!vendor.trim()) { setSaveError('Enter the supplier name — expenses and price history are both stored per vendor.'); return; }
    if (unset.length > 0) { setSaveError(unset.length + ' line' + (unset.length === 1 ? ' has' : 's have') + ' no destination - set each one to a job, an invoice, Business Expense, Truck Stock or Personal.'); return; }
    if (truckItems.length > 0 && !truckId) { setSaveError('Select a truck for the truck stock items.'); return; }
    if (addToInvoice && jobItems.length > 0 && !clientForDest(jobItems[0].destination)) { setSaveError('That target has no client - can not add to invoice.'); return; }
    if (addToInvoice && target === 'existing' && !invoiceId) { setSaveError('Select an invoice.'); return; }
    setSaving(true); setSaveError('');
    try {
      const date = purchaseDate || new Date().toISOString().slice(0, 10);

      // ONE EXPENSE ROW PER LINE ITEM, via the receipts endpoint. It writes the
      // receipt and every line inside one transaction, distributes the tax
      // across lines server-side, and returns a reconciliation block. The old
      // path collapsed everything into one aggregate row per destination with
      // the detail stuffed into notes as text, which made per-line category,
      // billable and job impossible.
      //
      // EVERY line goes in, including truck stock: you paid for it, so it is an
      // expense. Truck lines additionally increment inventory below - a cost
      // ledger and a quantity ledger, not a double count.
      const lines = items.map(it => ({
        desc: it.desc,
        qty: Number(it.qty) || 0,
        unit: it.unit || 'ea',
        unitCostPretax: Number(it.unitCost) || 0,
        category: it.category || 'Materials',
        // Personal resolves through SCHEDULE_C to the explicit non-deductible
        // sentence, so the row carries its own reason rather than a blank.
        scheduleC: SCHEDULE_C[it.category] || null,
        billable: isBillable(it.destination) ? !!it.billable : false,
        jobId: destKind(it.destination) === 'job' ? destRef(it.destination) : null,
        linkedInvoice: destKind(it.destination) === 'inv' ? destRef(it.destination) : null,
        clientId: clientForDest(it.destination),
      }));

      const receipt = await apiPost('/data/receipts', {
        vendor: vendor.trim(),
        purchaseDate: date,
        subtotal: Math.round(linesSubtotal * 100) / 100,
        taxAmount: Math.round(taxNum * 100) / 100,
        total: Math.round(computedTotal * 100) / 100,
        taxSource,
        notes: '',
        lines,
      });

      // The photo is what substantiates the figures. Upload failure must not
      // lose the receipt that was just written, so it is reported rather than
      // thrown - the rows are saved either way and the image can be added later.
      let photoWarning = '';
      if (photo && receipt && receipt.id) {
        const up = await uploadReceiptPhoto(receipt.id, photo);
        if (up.ok) {
          if (up.tries > 1) photoWarning = ' (the photo needed a second attempt)';
        } else {
          // Keep the image so Retry has something to send. setPhoto(null) below
          // clears the form; this holds its own reference and survives that.
          setPendingPhoto({ receiptId: receipt.id, vendor: vendor.trim(), photo, error: up.error });
          photoWarning = ' - but the photo did not upload, see below';
        }
      }

      for (const it of truckItems) {
        await apiPost('/data/inventory', { truckId: Number(truckId), desc: it.desc, unit: it.unit, qty: it.qty, unitCost: it.unitCost });
      }

      if (addToInvoice && jobItems.length > 0) {
        // Prefer the server's priced lines. Falls back to the local pre-tax
        // figures only if the response shape ever changes, so a bad deploy
        // under-bills rather than crashing.
        const priced = toArr(receipt && receipt.lines).filter(l => l.billable);
        const lineItems = priced.length
          ? priced.map(l => ({ type: 'material', desc: l.description, qty: Number(l.qty) || 0, unit: l.unit || 'ea',
              rate: Math.round(Number(l.unit_cost) * (1 + matMarkup / 100) * 100) / 100 }))
          : jobItems.map(it => ({ type: 'material', desc: it.desc, qty: it.qty, unit: it.unit, rate: Math.round(it.unitCost * (1 + matMarkup / 100) * 100) / 100 }));
        if (target === 'existing') {
          const inv = toArr(invoices).find(i => i.id === Number(invoiceId));
          openInvoiceModal(inv, null, lineItems);
        } else {
          openInvoiceModal(null, null, lineItems, jobClientId);
        }
      }

      if (savePrices && vendor.trim() && apiSaveMatPrices) {
        const newMatPriceHistory = { ...matPriceHistory };
        for (const it of items) {
          if (it.unitCost > 0 && it.desc && it.destination !== 'expense' && !isPersonal(it.destination)) {
            // Same fallback order as the price-sheet importer: flat map, then
            // the newest history entry, then nothing. Without the history leg
            // this is null for every material that came from a sheet.
            const oldPrice = (matPrices || {})[it.desc]
              ?? matPriceHistory?.[it.desc]?.[0]?.price
              ?? null;
            // The unit matters as much as the number. A price of 0.636 means
            // nothing without knowing it is per FOOT, and these entries sit
            // alongside sheet imports that do record it.
            // 🔑 A RECEIPT PRICES THE PACKAGE; THE PRICE BOOK STORES PER FOOT.
            // Buy a 100ft reel for $200 and the line reads 1 @ $200. Stored as-is
            // with a per-foot label, the next job that bills 15 ft would quote
            // $3,000 instead of $30. The sheet importer has divided since day one;
            // this path never did, and it put 62 bad entries in the book.
            const bk = bookPriceFor(it, vendor.trim(), oldPrice);
            const entry = { price: bk.price, oldPrice, supplier: vendor.trim(), date, unit: bk.unit };
            if (bk.pkgQty) { entry.pkgQty = bk.pkgQty; entry.pkgLabel = bk.pkgLabel; }
            // Keep what the paper said whenever a conversion happened, so a wrong
            // reading stays findable instead of looking like a quoted price.
            if (bk.converted) { entry.srcPrice = it.unitCost; entry.convertedBy = bk.via; }
            // What the receipt actually said, so a wrong reading stays findable.
            entry.srcQty = it.qty;
            entry.srcExtension = Math.round(it.qty * it.unitCost * 100) / 100;
            entry.fromReceipt = true;
            newMatPriceHistory[it.desc] = [entry, ...(newMatPriceHistory[it.desc] || [])].slice(0, 10);
          }
        }
        await apiSaveMatPrices({ ...matPrices }, newMatPriceHistory);
        setMatPriceHistory(newMatPriceHistory);
      }
      setSaved('Saved ' + lines.length + ' item' + (lines.length === 1 ? '' : 's') + photoWarning);
      setItems(null); setPhoto(null);
    } catch (e) { setSaveError('Failed to save: ' + e.message); }
    setSaving(false);
  };

  const retryPhoto = async () => {
    if (!pendingPhoto || retrying) return;
    setRetrying(true);
    const up = await uploadReceiptPhoto(pendingPhoto.receiptId, pendingPhoto.photo);
    if (up.ok) {
      setPendingPhoto(null);
      setSaved('Photo uploaded. Receipt ' + pendingPhoto.receiptId + ' now has its document.');
    } else {
      setPendingPhoto(p => (p ? { ...p, error: up.error } : p));
    }
    setRetrying(false);
  };

  const cell = { ...st.td, verticalAlign: 'top' };

  return (
    <div>
      <div style={st.h1}>Scan Receipt</div>
      <div style={{ color: D.muted, marginBottom: 16, fontSize: 13, maxWidth: 560 }}>
        Snap a photo of a receipt to itemize it. Each line is saved as its own expense with its own category, and the photo is kept as the record.
      </div>
      <div style={st.card}>
        <div onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
          onClick={() => { if (!scanning) { setSaved(false); fileRef.current?.click(); } }}
          style={{ border: '2px dashed ' + (dragOver ? D.accent : D.border), borderRadius: 10, padding: '28px 20px', textAlign: 'center', cursor: scanning ? 'default' : 'pointer', background: dragOver ? D.accent + '11' : 'transparent', transition: 'all 0.15s', userSelect: 'none' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
          <div style={{ fontWeight: 600, fontSize: 14, color: D.text, marginBottom: 4 }}>{scanning ? 'Scanning...' : 'Drop a receipt photo here'}</div>
          <div style={{ fontSize: 12, color: D.muted }}>or tap to open camera</div>
        </div>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={onFileChange} />
        {scanError && <div style={{ color: D.danger, fontSize: 13, marginTop: 10 }}>{scanError}</div>}
        {saved && <div style={{ color: D.success, fontSize: 13, marginTop: 10 }}>{typeof saved === 'string' ? saved : 'Saved!'} Scan another receipt or close this page.</div>}

        {/* The numbers are safe; the document is not, and it is the half that
            substantiates a deduction. Say plainly that leaving loses it. */}
        {pendingPhoto && (
          <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 6, border: '1px solid ' + D.warning, fontSize: 13, lineHeight: 1.5 }}>
            <div style={{ color: D.warning, fontWeight: 600 }}>The receipt saved, but its photo did not upload.</div>
            <div style={{ fontSize: 12, marginTop: 4, color: D.muted }}>
              {pendingPhoto.vendor ? pendingPhoto.vendor + ' \u2014 ' : ''}receipt {pendingPhoto.receiptId}. Tried twice. {pendingPhoto.error}
            </div>
            <div style={{ fontSize: 12, marginTop: 4, color: D.muted }}>
              The image is still held on this page. Leaving or reloading loses it, and the
              expense would keep no document.
            </div>
            <button style={{ ...st.btn(), marginLeft: 0, marginTop: 8 }} disabled={retrying} onClick={retryPhoto}>
              {retrying ? 'Uploading...' : 'Retry photo upload'}
            </button>
          </div>
        )}

        {items && (
          <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid ' + D.border }}>

            {/* Supplier. Required: expenses and price history are both keyed on
                it, and vendor totals are what reveal 1099 thresholds. */}
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
              <div style={{ minWidth: 240 }}>
                <Field label={<span>Supplier <span style={{ color: D.warning }}>*</span></span>}>
                  <input style={st.input} list="scan-supplier-names" value={vendor} placeholder="who you bought from"
                    onChange={e => setVendor(e.target.value)} />
                  {knownSuppliers.length > 0 && <datalist id="scan-supplier-names">{knownSuppliers.map(s => <option key={s} value={s} />)}</datalist>}
                </Field>
                {knownSuppliers.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                    {knownSuppliers.slice(0, 4).map(s => (
                      <button key={s} onClick={() => setVendor(s)}
                        style={{ ...st.btn(), marginLeft: 0, padding: '3px 9px', fontSize: 12, borderColor: vendor === s ? D.accent : D.border, color: vendor === s ? D.accent : D.muted }}>{s}</button>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ minWidth: 150 }}>
                <Field label="Date on receipt">
                  <input style={{ ...st.input, ...(dateNote && dateNote !== 'ok' ? { borderColor: D.warning } : {}) }}
                    type="date" value={purchaseDate}
                    onChange={e => { setPurchaseDate(e.target.value); setDateNote(dateVerdict(e.target.value)); }} />
                  {dateNote && dateNote !== 'ok' && (
                    <div style={{ fontSize: 11, color: D.warning, marginTop: 4, maxWidth: 280, lineHeight: 1.4 }}>
                      {dateNote === 'missing' && 'No date could be read - today\u2019s date filled in. Check it against the receipt.'}
                      {dateNote === 'future' && 'That date is in the future. Check it against the receipt.'}
                      {dateNote === 'old' && 'More than 45 days ago. Fine if you are catching up on an older receipt - otherwise check it.'}
                    </div>
                  )}
                </Field>
              </div>
              <div style={{ minWidth: 150 }}>
                <Field label="Sales tax">
                  <input style={st.input} type="number" step="0.01" min="0" value={tax} placeholder="0.00"
                    onChange={e => { setTax(e.target.value); setTaxSource('estimated'); }} />
                </Field>
                <div style={{ fontSize: 11, color: taxSource === 'read' ? D.muted : D.warning, marginTop: 4 }}>
                  {taxSource === 'read' ? 'read from the receipt' : 'not found on the scan — type it from the paper'}
                </div>
              </div>
            </div>

            {photo && (
              <div style={{ fontSize: 12, color: D.success, marginBottom: 12 }}>
                📎 Photo will be attached to this receipt
              </div>
            )}

            {vendor && apiSaveMatPrices && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', marginBottom: 14 }}>
                <input type="checkbox" checked={savePrices} onChange={e => setSavePrices(e.target.checked)} />
                Save material prices to supply house history
              </label>
            )}

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: D.muted }}>Set all to:</span>
              {FIXED_DESTS.map(d => (
                <button key={d.id} style={{ ...st.btn(), fontSize: 11, padding: '4px 10px' }} onClick={() => setAllDestinations(d.id)}>{d.label}</button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: D.muted }}>Category for all:</span>
              {QUICK_CATEGORIES.map(c => (
                <button key={c} style={{ ...st.btn(), fontSize: 11, padding: '4px 10px' }} onClick={() => setAllCategories(c)}>{c}</button>
              ))}
            </div>

            <div style={{ overflowX: 'auto', marginBottom: 16 }}>
              <table style={st.table}>
                <thead><tr>{['Item', 'Qty', 'Unit', 'Unit Cost', 'Category', 'Destination', 'Bill', ''].map(h => <th key={h} style={st.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i}>
                      <td style={cell}><input style={{ ...st.input, minWidth: 140 }} value={it.desc} onChange={e => updateItem(i, { desc: e.target.value })} /></td>
                      <td style={cell}><input style={{ ...st.input, width: 60 }} type="number" value={it.qty} onChange={e => updateItem(i, { qty: Number(e.target.value) || 0 })} /></td>
                      <td style={cell}><input style={{ ...st.input, width: 60 }} value={it.unit} onChange={e => updateItem(i, { unit: e.target.value })} /></td>
                      <td style={cell}>
                        <input style={{ ...st.input, width: 80 }} type="number" step="0.01" value={it.unitCost} onChange={e => updateItem(i, { unitCost: Number(e.target.value) || 0 })} />
                        {/* Counter prices drift from quoted prices far more than
                            the market does - the same part can come in 40-80%
                            apart on two tickets - so showing the standing price
                            beside the scanned one is where a bad line gets
                            caught, whether it is a misread or a genuine
                            overcharge. 20% is the threshold the pricing
                            research settled on. */}
                        {(() => {
                          const k = knownPrice(it.desc);
                          if (!(it.unitCost > 0)) return null;
                          // What this line will actually write to the price book. A stick
                          // price stored per foot is a 10x billing error, so the division
                          // is shown where it happens rather than only in the data.
                          const bk = bookPriceFor(it, vendor, k);
                          if (k == null && !bk.converted) return null;
                          // Compare the CONVERTED figure against the standing price - that
                          // is the number that will be stored, and a bad conversion shows
                          // up here in amber instead of silently.
                          const d = k == null ? null : (bk.price - k) / k;
                          const off = d != null && Math.abs(d) > 0.2;
                          return (
                            <div style={{ fontSize: 10, marginTop: 2 }}>
                              {bk.converted && (
                                <div style={{ color: D.accent }}>
                                  ÷ {bk.via} = ${bk.price < 1 ? bk.price.toFixed(4) : bk.price.toFixed(2)}/{bk.unit}
                                </div>
                              )}
                              {k != null && (
                                <div style={{ color: off ? D.warning : D.muted }}>
                                  was ${Number(k) < 1 ? Number(k).toFixed(4) : Number(k).toFixed(2)}
                                  {d != null && Math.abs(d) > 0.005 && <> ({d > 0 ? '+' : ''}{(d * 100).toFixed(0)}%)</>}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td style={cell}>
                        <select style={{ ...st.input, minWidth: 130 }} value={it.category}
                          disabled={isPersonal(it.destination)}
                          title={isPersonal(it.destination) ? 'A personal item has only one correct category - it is not a business expense of any kind.' : ''}
                          onChange={e => updateItem(i, { category: e.target.value })}>
                          {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td style={cell}>
                        <select style={{ ...st.input, minWidth: 240 }} value={it.destination}
                          onChange={e => updateItem(i, { destination: e.target.value, billable: isBillable(e.target.value), category: destCategory(e.target.value, it.category) })}>
                          <option value="">— choose —</option>
                          <optgroup label="Jobs">
                            {toArr(jobs).map(j => <option key={'j' + j.id} value={'job:' + j.id}>{j.title}{(j.clientId ?? j.client_id) ? ' — ' + clientName(j.clientId ?? j.client_id) : ''}</option>)}
                          </optgroup>
                          <optgroup label="Invoices (no job)">
                            {toArr(invoices).map(v => <option key={'i' + v.id} value={'inv:' + v.id}>{v.number}{(v.clientId ?? v.client_id) ? ' — ' + clientName(v.clientId ?? v.client_id) : ''}</option>)}
                          </optgroup>
                          {FIXED_DESTS.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
                        </select>
                      </td>
                      <td style={{ ...cell, textAlign: 'center' }}>
                        <input type="checkbox" checked={isBillable(it.destination) && !!it.billable}
                          disabled={!isBillable(it.destination)}
                          title={isBillable(it.destination) ? 'Rebill this line to the customer' : 'Only lines going to a job or an invoice can be rebilled'}
                          onChange={e => updateItem(i, { billable: e.target.checked })} />
                      </td>
                      <td style={cell}><button style={{ ...st.btn('danger'), fontSize: 11, padding: '2px 8px' }} onClick={() => removeItem(i)}>Remove</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals. The sum of the lines plus the tax must equal what the
                receipt says; when it does not, something was misread. Shown,
                never enforced. */}
            <div style={{ background: D.surface, borderRadius: 8, padding: '10px 12px', marginBottom: 16, maxWidth: 320, fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: D.muted }}><span>{items.length} line{items.length === 1 ? '' : 's'}</span><span>{money(linesSubtotal)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: D.muted }}><span>Sales tax</span><span>{money(taxNum)}</span></div>
              {personalTotal > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: D.warning }}
                  title="Recorded so this receipt still matches the photo, but it is not a business expense and is excluded from every deductible total.">
                  <span>of which personal</span><span>{money(personalTotal)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, marginTop: 4, paddingTop: 4, borderTop: '1px solid ' + D.border }}><span>Total</span><span>{money(computedTotal)}</span></div>
              {printedTotal != null && (
                <div style={{ marginTop: 6, fontSize: 11, color: totalsOff ? D.warning : D.muted }}>
                  Receipt says {money(printedTotal)}
                  {totalsOff && <> — off by {money(Math.abs(totalGap))}. Check for a missed line or a wrong quantity.</>}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                {/* Convenience only - the real choice is per line, in the table.
                    Picking here stamps every line at once, then override the
                    exceptions above. */}
                <Field label={'Set ALL ' + items.length + ' line' + (items.length === 1 ? '' : 's') + ' to'}>
                  <select style={{ ...st.input, minWidth: 320 }} value={jobId} onChange={e => { setJobId(e.target.value); setInvoiceId(''); if (e.target.value) setAllDestinations(e.target.value); }}>
                    <option value="">— set every line at once —</option>
                    <optgroup label="Jobs">
                      {toArr(jobs).map(j => <option key={'j' + j.id} value={'job:' + j.id}>{j.title}{(j.clientId ?? j.client_id) ? ' — ' + clientName(j.clientId ?? j.client_id) : ''}</option>)}
                    </optgroup>
                    <optgroup label="Invoices (work with no job)">
                      {toArr(invoices).map(i => <option key={'i' + i.id} value={'inv:' + i.id}>{i.number}{(i.clientId ?? i.client_id) ? ' — ' + clientName(i.clientId ?? i.client_id) : ''}</option>)}
                    </optgroup>
                  </select>
                </Field>
              </div>
            )}
            {truckItems.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <Field label={'Truck (for ' + truckItems.length + ' item' + (truckItems.length === 1 ? '' : 's') + ')'}>
                  <select style={st.input} value={truckId} onChange={e => setTruckId(e.target.value)}>
                    <option value="">— Select truck —</option>
                    {trucks.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </Field>
              </div>
            )}
            {jobItems.length > 0 && jobId && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', margin: '14px 0' }}>
                <input type="checkbox" checked={addToInvoice} onChange={e => setAddToInvoice(e.target.checked)} />
                Also add to invoice (with {matMarkup}% markup, billed amount: ${billedAmount.toFixed(2)})
              </label>
            )}
            {addToInvoice && jobItems.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                {!jobClientId && <div style={{ color: D.danger, fontSize: 12, marginBottom: 8 }}>This job has no client - choose a different job or uncheck "Add to invoice".</div>}
                <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="radio" checked={target === 'new'} onChange={() => setTarget('new')} /> New invoice
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: draftInvoices.length === 0 ? 'default' : 'pointer', color: draftInvoices.length === 0 ? D.muted : D.text }}>
                    <input type="radio" checked={target === 'existing'} onChange={() => setTarget('existing')} disabled={draftInvoices.length === 0} /> Add to existing draft {draftInvoices.length === 0 ? '(none for this client)' : ''}
                  </label>
                </div>
                {target === 'existing' && (
                  <Field label="Invoice">
                    <select style={st.input} value={invoiceId} onChange={e => setInvoiceId(e.target.value)}>
                      <option value="">— Select invoice —</option>
                      {draftInvoices.map(i => <option key={i.id} value={i.id}>{i.number} — {clientName(i.clientId ?? i.client_id)}</option>)}
                    </select>
                  </Field>
                )}
              </div>
            )}
            {!vendor.trim() && <div style={{ fontSize: 12, color: D.warning, marginBottom: 10 }}>Enter the supplier above before saving — expenses and price history are stored per vendor.</div>}
            {saveError && <div style={{ color: D.danger, fontSize: 13, marginBottom: 10 }}>{saveError}</div>}
            <button style={{ ...st.btn('primary'), marginLeft: 0 }} onClick={save} disabled={saving || items.length === 0 || !vendor.trim()}>{saving ? 'Saving...' : 'Save ' + items.length + ' item' + (items.length === 1 ? '' : 's')}</button>
          </div>
        )}
      </div>
    </div>
  );
}
